import { Box, Button, TextButton, Tooltip } from '@zillow/constellation-10';
import { IconArrowRightOutline } from '@zillow/constellation-icons';
import Link from 'next/link';
import { useContext } from 'react';
import { AppContext } from '../../../../contexts/AppContextProvider';
import { LeadManagementContext } from '../../../../contexts/LeadManagementContextProvider';
import { useCachedLeadByConversationId } from '../../../../hooks/useLeadManagementTableQuery';
import useInternalHref from '../../../../hooks/useInternalHref';
import useLeadManagementActions from '../../../../hooks/useLeadManagementActions';
import useDevices from '../../../../hooks/useDevices';
import useModal from '../../../../hooks/useModal';
import { ModalType } from '../../../../types/ModalType';
import {
  CLICK_ON_NEXT_STEP_COLUMN_EVENT_TYPE_ID,
  emitClickOnNextStepColumnEvent,
} from '../../../../constants/clickstreamEvents/leadManagementTable';
import ConditionalWrapper from '../../../ConditionalWrapper';
import NextStepHelperText from '../NextStepHelperText';
import { TOPIC_TAG_TXT } from '../../../../constants/clickstreamEvents/globals';
import { useAuth } from '../../../../AuthProvider';
import { InboxPageMobileView } from '../../../../types/InboxPageMobileView';
import { SatelliteNextStepType } from '@zg-rentals/ts-satellite-client';

type NextStepTextButtonProps = {
  conversationId: string;
  setActiveRowId?: (conversationId: string) => void;
  shouldDisplayAsButtonStyle?: boolean;
};

export default function NextStepTextButton({
  conversationId,
  shouldDisplayAsButtonStyle = false,
  setActiveRowId,
}: NextStepTextButtonProps) {
  const { isImpersonator } = useContext(AppContext);
  const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
  const { user } = useAuth();
  const { isMdAndBelow } = useDevices();
  const { setActiveModal } = useModal();
  const { handleArchiveClick, handleCallClick, handleInviteToApplyClick, handleOpenRenterMessagingModal } =
    useLeadManagementActions({
      conversationId,
    });

  const lead = useCachedLeadByConversationId(conversationId);
  const listingAlias = lead?.listingDetails?.listingAlias ?? '';

  const { nextStep, status } = lead || {};
  const { nextStepType, link } = nextStep || {};
  const { linkId, text, url: baseUrl } = link || {};
  const url =
    linkId === 'nextStepSendMessage' && baseUrl ? `${baseUrl}?mobileView=${InboxPageMobileView.MessageList}` : baseUrl;
  const { isConversationBlockedByZillow } = status || {};
  const { internalHref } = useInternalHref(url);
  const isDisabled = isArchivedFolder || isReportedFolder || isImpersonator || isConversationBlockedByZillow;
  const isMessageAction = !isMdAndBelow && linkId === 'nextStepSendMessage';
  const hasUrl = !isDisabled && nextStepType === SatelliteNextStepType.LINK && url && !isMessageAction;
  const shouldShowTooltip = linkId === 'nextStepInviteToApply' || isConversationBlockedByZillow || false;

  // With no resolved lead there is no label or action to render — an empty button reads as broken.
  if (!lead) {
    return null;
  }

  if (shouldDisplayAsButtonStyle && linkId === 'nextStepViewTourRequest') {
    return null;
  }

  const handleCallClickWrapper = () => {
    if (setActiveRowId) {
      setActiveRowId(conversationId);
    }
    handleCallClick();
  };

  const handleInviteToMoveInClick = () => {
    if (setActiveRowId) {
      setActiveRowId(conversationId);
    }
    setActiveModal(ModalType.LeaseOptionsModal);
  };

  // linkIds that kick off actions instead of navigating to a new page
  const linkIdToActionMap: Record<string, () => void> = {
    nextStepArchive: handleArchiveClick,
    nextStepCallPhone: handleCallClickWrapper,
    nextStepInviteToApply: handleInviteToApplyClick,
    nextStepInviteToMoveIn: handleInviteToMoveInClick,
    ...(!isMdAndBelow && {
      nextStepSendMessage: handleOpenRenterMessagingModal,
    }),
  };

  const linkIdToClickStreamTriggerObjectNameMap = new Map<string, string>([
    ['nextStepArchive', 'archive'],
    ['nextStepCallPhone', 'call'],
    ['nextStepInviteToApply', 'invite_to_apply'],
    ['nextStepInviteToMoveIn', 'invite_to_move_in'],
    ['nextStepInviteToPayments', 'invite_to_payments'],
    ['nextStepReviewApplication', 'review_application'],
    ['nextStepSendMessage', 'message'],
    ['nextStepSignTheLease', 'sign_the_lease'],
    ['nextStepViewTourRequest', 'view_tour_request'],
  ]);
  const triggerObjectName = linkIdToClickStreamTriggerObjectNameMap.get(linkId!) || '';
  const topicTag = linkId === 'nextStepViewTourRequest' ? TOPIC_TAG_TXT.ZrmTourScheduling : TOPIC_TAG_TXT.ZrmSatellite;
  const dataEventId = CLICK_ON_NEXT_STEP_COLUMN_EVENT_TYPE_ID;

  const nextStepTooltip = (
    <Tooltip.Root placement="bottom-start" shouldAwaitInteractionResponse>
      <Tooltip.Trigger />
      <Tooltip.Portal>
        <Tooltip.Content>
          <NextStepHelperText conversationId={conversationId} />
          <Tooltip.Arrow />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );

  const handleNextStepClickStreamEvent = () => {
    if (triggerObjectName) {
      emitClickOnNextStepColumnEvent(user, { listingAlias }, triggerObjectName, topicTag, {
        leadId: lead?.renterInfo?.leadId,
      });
    }
  };

  const handleNextStepActionClick = () => {
    const nextStepAction = linkIdToActionMap[linkId!];
    if (nextStepAction) {
      nextStepAction();
    }
    handleNextStepClickStreamEvent();
  };

  const renderNextStepButton = () => {
    if (hasUrl) {
      return (
        <Button
          data-eventid={dataEventId}
          emphasis="filled"
          asChild
          icon={<IconArrowRightOutline />}
          iconPosition="end"
          onClick={handleNextStepClickStreamEvent}
          tone="brand"
        >
          {internalHref ? <Link href={internalHref}>{text}</Link> : <a href={url}>{text}</a>}
        </Button>
      );
    } else {
      // Button style is the Renter Details Modal footer; its helper copy renders on a dedicated
      // row below the button group (see NextStepHelperText there), not in the table/cards tooltip.
      return (
        <Button
          data-eventid={dataEventId}
          emphasis="filled"
          disabled={isDisabled}
          onClick={handleNextStepActionClick}
          icon={<IconArrowRightOutline />}
          iconPosition="end"
          tone="brand"
        >
          {text}
        </Button>
      );
    }
  };
  const renderNextStepTextButton = () => {
    if (hasUrl) {
      return (
        <TextButton data-eventid={dataEventId} asChild textStyle="body-bold" onClick={handleNextStepClickStreamEvent}>
          {internalHref ? <Link href={internalHref}>{text}</Link> : <a href={url}>{text}</a>}
        </TextButton>
      );
    } else {
      return (
        <ConditionalWrapper
          shouldRenderWrapperElement={shouldShowTooltip}
          wrapper={(children) => (
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
              {children}
              {nextStepTooltip}
            </Box>
          )}
        >
          <TextButton
            data-eventid={dataEventId}
            textStyle="body-bold"
            disabled={isDisabled}
            onClick={handleNextStepActionClick}
          >
            {text}
          </TextButton>
        </ConditionalWrapper>
      );
    }
  };

  return shouldDisplayAsButtonStyle ? renderNextStepButton() : renderNextStepTextButton();
}
